'use client'

import { useState, useMemo } from 'react'
import { cn } from '@paitox/ui/lib/utils'
import {
  Search, Plus, Clock, Activity, Trash2, Edit
} from 'lucide-react'
import { db, Service as DbService } from '../lib/db'

const CATEGORY_COLORS: Record<string, string> = {
  facial: 'bg-cyan-50 text-cyan-700 border border-cyan-200',
  masajes: 'bg-violet-50 text-violet-700 border border-violet-200',
  uñas: 'bg-rose-50 text-rose-700 border border-rose-200',
  depilación: 'bg-emerald-50 text-emerald-700 border border-emerald-200'
}

const fmtCLP = (n: number) => `$${n.toLocaleString('es-CL')}`

export function ServicesPage() {
  const [services, setServices] = useState<DbService[]>(() => db.getServices())
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [showNew, setShowNew] = useState(false)

  // Creation fields
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [durationMin, setDurationMin] = useState(60)
  const [bufferMin, setBufferMin] = useState(10)
  const [price, setPrice] = useState(30000)
  const [category, setCategory] = useState('facial')

  const filtered = useMemo(() => {
    let list = services
    if (search) {
      list = list.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || s.description.toLowerCase().includes(search.toLowerCase()))
    }
    if (categoryFilter) {
      list = list.filter(s => s.category === categoryFilter)
    }
    return list
  }, [services, search, categoryFilter])

  const handleCreate = () => {
    if (!name) return

    const newService: DbService = {
      id: `srv-${Date.now()}`,
      name,
      description,
      durationMin,
      bufferMin,
      price,
      category,
      isActive: true
    }

    const updated = [...services, newService]
    setServices(updated)
    db.saveServices(updated)
    setShowNew(false)

    // Reset fields
    setName('')
    setDescription('')
    setDurationMin(60)
    setBufferMin(10)
    setPrice(30000)
    setCategory('facial')
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[hsl(220,18%,96%)] p-4 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-lg font-bold text-slate-800">Servicios</h1>
          <p className="text-xs text-slate-500">{services.length} servicios registrados</p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 h-9 text-sm font-semibold rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 transition shadow-sm">
          <Plus className="h-4 w-4" /> Nuevo servicio
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap shrink-0">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar servicios..."
            className="w-full h-9 pl-9 pr-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400 transition"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {['facial', 'masajes', 'uñas', 'depilación'].map(cat => (
            <button key={cat} onClick={() => setCategoryFilter(c => c === cat ? null : cat)}
              className={cn('px-2.5 h-8 rounded-lg text-xs font-semibold border transition-all capitalize',
                categoryFilter === cat ? CATEGORY_COLORS[cat] || 'bg-slate-850 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300')}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Services list */}
      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(service => (
            <div key={service.id} className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 hover:shadow-md transition-all group flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start gap-2">
                  <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize border', CATEGORY_COLORS[service.category])}>
                    {service.category}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    <span className="text-[10px] font-semibold text-emerald-700">Activo</span>
                  </div>
                </div>
                <h3 className="font-bold text-sm text-slate-800 mt-2 group-hover:text-cyan-600 transition-colors">{service.name}</h3>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{service.description}</p>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1 text-slate-500">
                    <Clock className="h-3.5 w-3.5" />
                    <span className="text-xs font-medium">{service.durationMin} min</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-500">
                    <Activity className="h-3.5 w-3.5" />
                    <span className="text-xs font-medium">+{service.bufferMin}m buffer</span>
                  </div>
                </div>
                <div className="text-sm font-extrabold text-cyan-600">
                  {fmtCLP(service.price)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Creation Modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowNew(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md p-6">
            <h3 className="text-base font-bold text-slate-800 mb-4">Añadir Nuevo Servicio</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Nombre *</label>
                <input type="text" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Descripción</label>
                <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" rows={2} value={description} onChange={e => setDescription(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Duración (min) *</label>
                  <input type="number" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={durationMin} onChange={e => setDurationMin(Number(e.target.value))} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Buffer (min) *</label>
                  <input type="number" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={bufferMin} onChange={e => setBufferMin(Number(e.target.value))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Precio (CLP) *</label>
                  <input type="number" className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={price} onChange={e => setPrice(Number(e.target.value))} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Categoría *</label>
                  <select className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm" value={category} onChange={e => setCategory(e.target.value)}>
                    <option value="facial">Facial</option>
                    <option value="masajes">Masajes</option>
                    <option value="uñas">Uñas</option>
                    <option value="depilación">Depilación</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowNew(false)} className="flex-1 h-9 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition">Cancelar</button>
              <button onClick={handleCreate} disabled={!name} className="flex-1 h-9 bg-cyan-600 text-white rounded-xl text-sm font-semibold hover:bg-cyan-700 transition disabled:opacity-50">Guardar Servicio</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
